/**
 * "Start" for one stopped service, in place where a tab says it is not running (the API tab): the same start as the
 * service's own Start (startServices, followed in startRuns.js so it goes on when this unmounts), its progress in the
 * button's popover; `onStarted` once it runs (the tab reloads), a failure said in one line. A service flagged No build
 * (context only) is never started: "Context only" instead.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { markHomeStep } from '../../services/SettingsService';
import { startServices } from './startProgress';
import StartButton from './StartButton';
import { clearStart, runStart, serviceStartKey, useStart } from './startRuns';
import useRepoFlags from '../../hooks/useRepoFlags';
import { NO_BUILD_TIP } from '../repoFlags/RepoFlagBadges';

const shortName = (serviceName) => String(serviceName).replace(/^services\//, '');

export default function ServiceStartButton({ projectId, serviceName, onStarted = undefined, size = 'small', pollMs = undefined }) {
  const key = serviceStartKey(projectId, serviceName);
  const { progress, starting } = useStart(key);
  const [error, setError] = useState(null);
  const contextOnly = Boolean(useRepoFlags(projectId).serviceFlags(serviceName)?.noBuild);
  const start = () => {
    setError(null);
    runStart(key, (onProgress) => startServices(projectId, [{ serviceName, service: null }], { pollMs, onProgress }))
      .then((last) => {
        const entry = last?.services?.[0];
        if (entry && (entry.phase === 'failed' || entry.phase === 'limit')) {
          setError(entry.phase === 'limit' ? entry.reason : `${shortName(serviceName)} could not start: ${entry.reason || 'see Logs'}`);
          return;
        }
        markHomeStep('previewOpened');
        clearStart(key);
        onStarted?.();
      })
      .catch((err) => setError(err?.message || 'It could not start'));
  };
  if (contextOnly) {
    return <span className="repo-flag-context-only" data-testid="service-context-only" title={NO_BUILD_TIP}>Context only</span>;
  }
  return (
    <span className="service-start-inline">
      <StartButton
        ariaLabel={`Start ${shortName(serviceName)}`}
        disabled={starting}
        label="Start"
        onStart={start}
        progress={starting ? progress : null}
        size={size}
      />
      {error && <span className="sbx-action-error" role="alert">{error}</span>}
    </span>
  );
}

ServiceStartButton.propTypes = {
  onStarted: PropTypes.func,
  pollMs: PropTypes.number,
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
};
